/**
 * itemClipboard.js — pure model helpers behind the item Copy / Paste / Duplicate
 * keyboard flow (Ctrl+C / Ctrl+V / Ctrl+D, plus Delete).
 *
 * The clipboard holds ONE item's layout state ({ type, side?, index?, id?, data }).
 * Paste is TARGET-AWARE: with an item of the same type selected, the copied
 * state lands on the SELECTED item (its identity — id / roster slot — is kept);
 * with no same-type selection it falls back to the copied item's own slot.
 * That is what makes "copy this player's setup, select another player, paste"
 * work the way the guide describes, instead of always re-pasting onto the source.
 *
 * Duplicate clones a ball / cone / target / zone in the layout phase with a
 * fresh sequential id, nudged 1 m diagonally so the copy is visible and grabbable.
 * Players are not duplicable (fixed 1–11 roster per side) — copy/paste between
 * roster slots is the way to reuse a player's setup.
 */

const ITEM_LISTS = { ball: 'balls', cone: 'cones', target: 'targets', zone: 'zones' };

/** Nudge (metres) applied to a duplicate so it never lands exactly on its source. */
export const DUPLICATE_OFFSET = 1;

/**
 * Shift a copied movement ROUTE so it keeps the same relationship to the
 * target player's anchor spot that it had to the SOURCE player's base position
 * (`route.anchor`, recorded at copy time — falls back to the first key). Times
 * are preserved; only x/z move. Inherited LOCKED keys (child branches) survive:
 * a locked key is kept unless the route has a key at the same time (which, by
 * construction, sits at the anchor anyway). Pure — unit-tested.
 */
export function shiftRoute(route, anchorPosition, lockedKeys = []) {
  const from = route?.anchor || route?.position?.[0]?.position;
  const dx = anchorPosition && from ? anchorPosition[0] - from[0] : 0;
  const dz = anchorPosition && from ? anchorPosition[2] - from[2] : 0;
  const pasted = (route?.position || []).map(k => ({
    ...k,
    position: [k.position[0] + dx, k.position[1], k.position[2] + dz],
  }));
  const kept = (lockedKeys || []).filter(lk => !pasted.some(p => Math.abs(p.time - lk.time) < 1e-9));
  return {
    position: [...kept, ...pasted].sort((a, b) => a.time - b.time),
    segments: JSON.parse(JSON.stringify(route?.segments || [])),
  };
}

/**
 * Where a paste should land: the selected item when it shares the clipboard's
 * type, otherwise the clipboard's own slot. Returns null with no clipboard.
 */
export function resolvePasteTarget(clipboard, selectedItem) {
  if (!clipboard) return null;
  if (selectedItem && selectedItem.type === clipboard.type) {
    if (clipboard.type === 'player') {
      if (selectedItem.side === undefined || selectedItem.index === undefined) return null;
      return { type: 'player', side: selectedItem.side, index: selectedItem.index };
    }
    if (selectedItem.id !== undefined && selectedItem.id !== null) {
      return { type: clipboard.type, id: selectedItem.id };
    }
  }
  return {
    type: clipboard.type,
    side: clipboard.side,
    index: clipboard.index,
    id: clipboard.id,
  };
}

/**
 * Apply the clipboard's data to `start` (the layout phase's pitch state) at the
 * resolved target. The target keeps its own identity: a pasted item's `id` is
 * rewritten to the target's id, and a player paste writes into the target's
 * roster slot. Returns a NEW start object (never mutates the input).
 */
export function applyPasteToStart(start, clipboard, target) {
  if (!start || !clipboard || !target) return start;
  const data = JSON.parse(JSON.stringify(clipboard.data || {}));

  if (target.type === 'player') {
    const sideState = start[target.side] || {};
    const players = [...(sideState.players || [])];
    while (players.length <= target.index) players.push({ position: null });
    players[target.index] = data;
    return { ...start, [target.side]: { ...sideState, players } };
  }

  const listKey = ITEM_LISTS[target.type];
  if (!listKey) return start;
  data.id = target.id; // the target keeps its identity — never the source's id
  const list = start[listKey] || [];
  const exists = list.some(x => x && x.id === target.id);
  return {
    ...start,
    [listKey]: exists
      ? list.map(x => (x && x.id === target.id ? data : x))
      : [...list, data],
  };
}

/** Next sequential id for a type within one pitch state ('ball-03' style). */
export function nextItemIdInStart(type, start) {
  const listKey = ITEM_LISTS[type];
  const list = (start && listKey ? start[listKey] : []) || [];
  let max = 0;
  list.forEach(x => {
    const m = String(x?.id ?? '').match(/(\d+)$/);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  return `${type}-${String(max + 1).padStart(2, '0')}`;
}

/**
 * Clone a ball / cone / target / zone inside `start`. Returns
 * { start, item } — the new pitch state and the descriptor of the duplicate —
 * or null when the item cannot be duplicated (players, missing item, unknown type).
 */
export function duplicateItemInStart(start, item) {
  const listKey = ITEM_LISTS[item?.type];
  if (!listKey || !start) return null;
  const list = start[listKey] || [];
  const src = list.find(x => x && x.id === item.id);
  if (!src) return null;

  const newId = nextItemIdInStart(item.type, start);
  const clone = JSON.parse(JSON.stringify(src));
  clone.id = newId;
  const off = DUPLICATE_OFFSET;
  if (Array.isArray(clone.position)) {
    clone.position = [clone.position[0] + off, clone.position[1] ?? 0, clone.position[2] + off];
  }
  if (Array.isArray(clone.points)) {
    clone.points = clone.points.map(p => [p[0] + off, p[1] ?? 0, p[2] + off]);
  }
  if (Array.isArray(clone.pointRefs)) {
    // Vertex attachments belong to the source's shape — a fresh copy starts free.
    clone.pointRefs = clone.pointRefs.map(() => ({ ref: null }));
  }
  if ('attached' in clone) clone.attached = null;

  return {
    start: { ...start, [listKey]: [...list, clone] },
    item: { type: item.type, id: newId },
  };
}
